import {inspectArtwork, findVariant, buildLinePayload, uploadLine} from './serve-custom-core.js';

class ServeCustomShop extends HTMLElement {
  connectedCallback() {
    if (this.started) return;
    this.started = true;
    this.abort = new AbortController();
    this.generation = 0;
    this.messages = JSON.parse(this.querySelector('[data-messages]').textContent);
    this.form = this.querySelector('form');
    this.orderingEnabled = this.dataset.orderingEnabled === 'true';
    this.file = this.querySelector('[type=file]');
    this.preview = this.querySelector('[data-preview]');
    this.status = this.querySelector('[data-status]');
    this.review = this.querySelector('[data-review]');
    this.summary = this.querySelector('[data-summary]');
    this.listen(this.form, 'submit', event => event.preventDefault());
    this.listen(this.file, 'change', () => this.checkImage());
    this.listen(this.querySelector('[data-remove]'), 'click', () => this.clearImage());
    this.listen(this.review, 'click', () => this.showReview());
    this.listen(this.querySelector('[data-add]'), 'click', () => this.addToCart());
    this.listen(this.form, 'change', () => {this.summary.hidden = true;});
    this.listen(this.form, 'input', () => {this.summary.hidden = true;});
    this.querySelectorAll('[data-garment]').forEach(button => this.listen(button, 'click', () => this.selectGarment(button.dataset.garment)));
    fetch(this.dataset.catalog, {signal:this.abort.signal}).then(response => {
      if (!response.ok) throw new Error('error');
      return response.json();
    }).then(catalog => {
      this.catalog = catalog;
      catalog.forEach(item => {
        const holder = this.querySelector('[data-garment="' + item.key + '"] [data-garment-photo]');
        if (!holder || !item.image) return;
        const image = document.createElement('img');
        image.src = item.image;
        image.alt = '';
        image.width = 400;
        image.height = 400;
        image.loading = 'lazy';
        image.decoding = 'async';
        holder.replaceChildren(image);
      });
      this.selectGarment(catalog.find(item => item.productId === this.dataset.currentProduct)?.key || catalog[0].key);
    }).catch(error => {
      if (error.name !== 'AbortError') this.status.textContent = this.messages.error;
    });
  }

  listen(node, name, callback) {
    node.addEventListener(name, callback, {signal:this.abort.signal});
  }

  disconnectedCallback() {
    this.abort?.abort();
    if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);
    this.generation++;
    this.started = false;
  }

  clearImage() {
    this.generation++;
    this.artwork = null;
    this.file.value = '';
    this.file.setCustomValidity('');
    if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);
    this.objectUrl = null;
    this.preview.removeAttribute('src');
    this.preview.hidden = true;
    this.querySelector('[data-preview-empty]').hidden = false;
    this.querySelector('[data-filename]').textContent = '';
    this.querySelector('[data-remove]').hidden = true;
    this.status.textContent = '';
    this.summary.hidden = true;
  }

  selectGarment(key) {
    if (!this.catalog) return;
    this.selected = this.catalog.find(item => item.key === key);
    if (!this.selected) return;
    this.clearImage();
    this.querySelectorAll('[data-garment]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.garment === key)));
    this.querySelector('[data-selected-title]').textContent = this.selected.title;
    this.querySelector('[data-price]').textContent = new Intl.NumberFormat('en-US',{style:'currency',currency:'USD'}).format(Number(this.selected.price));
    this.querySelector('[data-confirmation]').hidden = !this.selected.requiresConfirmation;
    const bottle = this.selected.type === 'bottle' || this.selected.type === 'mug';
    this.querySelector('[data-methods]').hidden = bottle;
    this.querySelectorAll('[data-method]').forEach(input => {input.checked = false; input.disabled = bottle;});
    this.querySelector('[data-bottle-methods]').hidden = !bottle;
    this.querySelector('[data-bottle-tiers]').hidden = !bottle;
    this.querySelector('[data-bottle-tiers] p').textContent = this.selected.tierText || '';
    const container = this.querySelector('[data-options]');
    container.replaceChildren();
    this.selected.options.forEach((name,index) => {
      const values = [...new Set(this.selected.variants.map(v => v.options[index]))];
      if (name.toLowerCase() === 'color') {
        const group = document.createElement('fieldset');
        group.className = 'serve-custom__colors';
        group.dataset.option = String(index);
        const legend = document.createElement('legend');
        legend.textContent = name;
        group.append(legend);
        const pills = document.createElement('div');
        pills.className = 'serve-custom__pills';
        values.forEach(value => {
          const label = document.createElement('label');
          label.className = 'serve-custom__pill';
          const radio = document.createElement('input');
          radio.type = 'radio';
          radio.name = this.file.id + '-color-' + index;
          radio.value = value;
          radio.required = true;
          const face = document.createElement('span');
          face.className = 'serve-custom__pill-face';
          const swatch = document.createElement('span');
          swatch.className = 'serve-custom__swatch';
          swatch.setAttribute('aria-hidden', 'true');
          swatch.style.background = this.selected.swatches[value];
          const text = document.createElement('span');
          text.textContent = value;
          const check = document.createElement('span');
          check.className = 'serve-custom__pill-check';
          check.setAttribute('aria-hidden', 'true');
          check.textContent = '✓';
          face.append(swatch, text, check);
          label.append(radio, face);
          pills.append(label);
        });
        group.append(pills);
        container.append(group);
        return;
      }
      const label = document.createElement('label');
      label.textContent = name;
      const select = document.createElement('select');
      select.required = true;
      select.dataset.option = String(index);
      const prompt = document.createElement('option');
      prompt.value = '';
      prompt.textContent = this.messages.select + ' ' + name.toLowerCase();
      select.append(prompt);
      values.forEach(value => {
        const option = document.createElement('option');
        option.value = value;
        option.textContent = value;
        select.append(option);
      });
      label.append(select);
      container.append(label);
    });
    const updateChoices = changed => {
      const controls = [...container.querySelectorAll('[data-option]')];
      for (let index = changed + 1; index < controls.length; index++) {
        const control = controls[index];
        const previous = this.optionValues().slice(0,index);
        const allowed = new Set(this.selected.variants.filter(v => previous.every((value,k) => value && v.options[k] === value)).map(v => v.options[index]));
        if (control.tagName === 'SELECT') {
          [...control.options].forEach(option => {option.hidden = Boolean(option.value) && !allowed.has(option.value);option.disabled = option.hidden;});
          if (!allowed.has(control.value)) control.value = '';
        } else {
          control.querySelectorAll('input').forEach(radio => {
            radio.disabled = !allowed.has(radio.value);
            if (radio.disabled) radio.checked = false;
          });
        }
        control.disabled = allowed.size === 0;
      }
      const variant = findVariant(this.selected.variants, this.optionValues());
      this.querySelector('[data-price]').textContent = new Intl.NumberFormat('en-US',{style:'currency',currency:'USD'}).format(Number(variant?.price || this.selected.price));
    };
    [...container.querySelectorAll('[data-option]')].forEach((control,index) => this.listen(control, 'change', () => updateChoices(index)));
    updateChoices(-1);
    this.querySelector('[data-no-options]').hidden = Boolean(this.selected.options.length);
  }

  optionValues() {
    return [...this.querySelectorAll('[data-option]')].map(control =>
      control.tagName === 'SELECT' ? control.value : control.querySelector('input:checked')?.value || ''
    );
  }

  async checkImage() {
    const file = this.file.files[0];
    const revision = ++this.generation;
    this.artwork = null;
    this.file.setCustomValidity('');
    this.status.textContent = this.messages.loading;
    this.summary.hidden = true;
    if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);
    this.objectUrl = null;
    this.preview.hidden = true;
    this.querySelector('[data-preview-empty]').hidden = false;
    this.querySelector('[data-filename]').textContent = '';
    this.querySelector('[data-remove]').hidden = true;
    try {
      const artwork = await inspectArtwork(file);
      if (revision !== this.generation) return;
      const url = URL.createObjectURL(file);
      try {
        const decoded = new Image();
        decoded.src = url;
        await decoded.decode();
        if (!decoded.naturalWidth || !decoded.naturalHeight) throw new Error('file_invalid');
        if (decoded.naturalWidth * decoded.naturalHeight > 40000000) throw new Error('file_pixels');
        if (revision !== this.generation) {URL.revokeObjectURL(url); return;}
      } catch (error) {URL.revokeObjectURL(url); throw error;}
      this.artwork = artwork;
      this.objectUrl = url;
      this.preview.src = url;
      this.preview.hidden = false;
      this.querySelector('[data-preview-empty]').hidden = true;
      this.querySelector('[data-filename]').textContent = file.name;
      this.querySelector('[data-remove]').hidden = false;
      this.status.textContent = '';
    } catch(error) {
      if (revision !== this.generation) return;
      const message = this.messages[error.message] || this.messages.file_invalid;
      this.file.setCustomValidity(message);
      this.status.textContent = message;
    }
  }

  showReview() {
    if (!this.selected || !this.form.reportValidity()) return;
    if (!this.artwork) {this.status.textContent = this.messages.file_required; this.file.focus(); return;}
    const methods = [...this.querySelectorAll('[data-method]:checked')].map(input => input.value);
    if (this.selected.type === 'garment' && !methods.length) {this.status.textContent = this.messages.method_required; this.querySelector('[data-method]').focus(); return;}
    const choices = this.optionValues();
    if (!findVariant(this.selected.variants, choices)) {this.status.textContent = this.messages.need_options; return;}
    const description = this.querySelector('textarea').value.trim();
    if (!description) {this.status.textContent = this.messages.description_required; this.querySelector('textarea').focus(); return;}
    this.summary.replaceChildren();
    const heading = document.createElement('h3');
    heading.textContent = this.messages.review_label;
    this.summary.append(heading);
    const values = [this.selected.title, choices.join(' / '), this.selected.type !== 'garment' ? this.querySelector('[data-bottle-tiers]').textContent : '$' + this.selected.price, this.querySelector('[data-quantity]').value + ' × ' + this.selected.title, this.artwork.file.name, methods.join('; '), description, this.messages.ready];
    values.filter(Boolean).forEach(value => {const p = document.createElement('p');p.textContent = value;this.summary.append(p);});
    this.summary.hidden = false;
    this.summary.focus();
    this.status.textContent = '';
    return {choices, methods, description, quantity:Number(this.querySelector('[data-quantity]').value), artwork:this.artwork, product:{...this.selected}};
  }

  async addToCart() {
    if (!this.orderingEnabled || this.sending || this.uncertainCart) return;
    const selection = this.showReview();
    if (!selection) return;
    this.sending = true;
    const controls = [...this.form.querySelectorAll('input,select,textarea,button')];
    const disabled = controls.map(control => control.disabled);
    controls.forEach(control => {control.disabled = true;});
    this.status.textContent = this.messages.order_busy;
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(root + 'products/' + encodeURIComponent(selection.product.handle) + '.js', {cache:'no-store'});
      if (!response.ok) throw new Error('variant_unavailable');
      const liveProduct = await response.json();
      if (String(liveProduct.id) !== selection.product.productId) throw new Error('variant_unavailable');
      const variant = findVariant(liveProduct.variants, selection.choices);
      const payload = buildLinePayload({...selection, variant, requireMethods:selection.product.type === 'garment'});
      this.uncertainCart = true;
      await uploadLine(payload, {root, orderingEnabled:true});
      window.location.assign(root + 'cart');
    } catch(error) {
      this.status.textContent = this.messages[error.message] || this.messages.order_error;
    } finally {
      this.sending = false;
      if (!this.uncertainCart) controls.forEach((control,i) => {control.disabled = disabled[i];});
    }
  }
}

if (!customElements.get('serve-custom-shop')) customElements.define('serve-custom-shop', ServeCustomShop);

